import { useEffect, useRef } from 'react';
import { useUploadJobStore } from '@/stores/uploadJobStore';

const TERMINAL_TOAST_MS = 8000;

/**
 * Headless lifecycle owner for upload jobs, mounted once above the router so it
 * runs on every route. It warns on tab close while an upload is in flight, drops
 * cancelled jobs, and auto-dismisses terminal jobs after a short window. The
 * visual progress cards render in the sidebar (SidebarUploadPanel).
 */
export function UploadJobController() {
  const jobs = useUploadJobStore((s) => s.jobs);
  const terminalTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // A hard tab close / reload loses in-flight jobs (they live in this SPA session),
  // so warn while any is active. In-app navigation stays free.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (useUploadJobStore.getState().hasActiveJob()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Reconcile lifecycle: drop cancelled jobs, auto-dismiss a SUCCESSFUL job after a short
  // window, and clear timers for jobs that have left the map. Failed jobs are left in place
  // so the error and its Retry are not yanked away before the clinician can act. No unmount
  // cleanup: the controller lives for the whole session, and a late timer only calls dismiss
  // (a no-op if the job is already gone).
  useEffect(() => {
    Object.values(jobs).forEach((job) => {
      if (job.phase === 'cancelled') {
        useUploadJobStore.getState().dismiss(job.key);
        return;
      }
      if (job.phase === 'complete' && !terminalTimers.current.has(job.key)) {
        const timer = setTimeout(() => {
          terminalTimers.current.delete(job.key);
          useUploadJobStore.getState().dismiss(job.key);
        }, TERMINAL_TOAST_MS);
        terminalTimers.current.set(job.key, timer);
      }
    });
    terminalTimers.current.forEach((timer, key) => {
      if (!jobs[key]) {
        clearTimeout(timer);
        terminalTimers.current.delete(key);
      }
    });
  }, [jobs]);

  return null;
}
